import type { Feature } from 'geojson'
import { atom } from 'jotai'
import { atomWithLocation } from 'jotai-location'
import { atomWithStorage } from 'jotai/utils'
import type { Filter, Sort } from 'mongodb'
import type {
  AppMapDrawController,
  AppMapRef,
  AppMapStyle,
  AppMapViewState,
} from '~/components'
import type { Listing, LotSizeUnit } from '~/models'
import { DataLayerEntry } from '~/models'
import { buildMapPropertyUseClause } from '~/models/mapPropertyUse'
import {
  buildMapRootPath,
  escapeRegex,
  generateAddressVariations,
  generateCityNameVariations,
  isMapCollectionPathname,
} from '~/utils'
import { aOrganization, aUser } from './app'

type DrawnFeature = Feature | null

export const aDrawnFeature = atom<DrawnFeature>(null)

export const aMapRef = atom<AppMapRef | null>(null)
export const aMapDrawRef = atom<AppMapDrawController | null>(null)
export const aMapDrawIsOpen = atom(false)
export const aMapZoomNextListingLoad = atom(false)

export const aMapStyle = atomWithStorage<AppMapStyle>('mapStyle', 'street')

export const aMapViewState = atom<AppMapViewState | null>(null)
export const aIsMapAutoRepositioning = atom<boolean>(false)
export const aGoogleMapSimplifiedMode = atom(false)
export const aGoogleMapIsPanning = atom(false)
export const aGoogleMapIsWheelZooming = atom(false)
export const aGoogleMapIsZooming = atom(false)
export const aGoogleStreetViewVisible = atom(false)

export const aFiltersPinned = atomWithStorage<boolean>(
  'mapFiltersPinned',
  false
)

export const aCardsCollapse = atomWithStorage<'single' | 'double' | 'none'>(
  'mapCardsCollapse',
  'double'
)

export type SelectedListing = {
  _id: string
  slug: string
  status: string
  type: string
  propertyTypes: string[]
  location: {
    coordinates: number[]
  }
}

export const aSelectedListing = atom<SelectedListing | Listing | null>(null)

export type HoverFeature = {
  address?: string
  isBusinessListing?: boolean
  lat: number
  lng: number
  name: string
  propertyTypes: string
  status: string
  type: string
  _id: string
  thumbUrl?: string
}
export const aHoverFeature = atom<HoverFeature | null>(null)
export const aMarkLocation = atom<boolean>(false)
export const aIsMapEasing = atom<boolean>(false)

export const aMapCurrentCount = atom<number | null>(null)
export const aCheckedListings = atom<string[]>([])
export const aHiddenListings = atom<string[]>([])

export const aDrawMode = atom<string | null>(null)

export const aParcelDataVisible = atomWithStorage<boolean>(
  'parcelDataVisible',
  false
)
export const aParcelDataResidentialVisible = atomWithStorage<boolean>(
  'parcelDataResidentialVisible',
  false
)
export const aVisibleLayers = atom<string[]>([])
export const aHoverDataLayerEntry = atom<DataLayerEntry | null>(null)

export const aVisibleGeographies = atomWithStorage<string[]>(
  'visibleGeographies',
  []
)

// Internal url tracking. Used by aMapFilters to get/set url params
const aMapLocation = atomWithLocation()
export const aInitialMapSearch = atom<string | null>(null)

export const aDefaultMapFilters = atom((get): MapFiltersType => {
  if (get(aUser) && get(aOrganization)?.options.appraisersOnly) {
    return {
      types: ['sale', 'lease'],
      statuses: ['sold', 'leased'],
    }
  }
  return {
    types: ['sale', 'lease'],
    statuses: ['active', 'pending'],
  }
})

export const PUBLIC_MAP_STATUSES = ['active', 'pending'] as const

export type RateType = 'annual' | 'monthly' | 'totalMonthly'
export type PriceType = 'sf' | 'acre' | 'total'

export type MapFiltersType = {
  lat?: string
  lng?: string
  z?: string
  b?: string
  p?: string
  search?: string
  addressSearch?: string
  types?: string[]
  statuses?: string[]
  propertyTypes?: string[]
  propertySubtypes?: string[]
  submarkets?: string[]
  zoning?: string[]
  prices?: (number | undefined)[]
  priceType?: PriceType
  caps?: (number | undefined)[]
  rates?: (number | undefined)[]
  rateType?: RateType
  bsf?: (number | undefined)[]
  sf?: (number | undefined)[]
  acres?: (number | undefined)[]
  lotSizeUnit?: LotSizeUnit
  companies?: string[]
  brokers?: string[]
  created?: (string | undefined)[]
  modified?: (string | undefined)[]
  transacted?: (string | undefined)[]
  sort?: string
  postalCodes?: string[]
  cities?: string[]
  counties?: string[]
  states?: string[]
  business?: 'true' | 'hide'

  officeSf?: (number | undefined)[]
  officeCeilingHeight?: (number | undefined)[]
  dockNumber?: (number | undefined)[]
  driveInNumber?: (number | undefined)[]
  trailerParkingSpaces?: (number | undefined)[]
  ceilingHeight?: (number | undefined)[]
  clearHeight?: (number | undefined)[]
  eaveHeight?: (number | undefined)[]
  overheadCranes?: (number | undefined)[]
  railDoors?: (number | undefined)[]
  // lighting? (string | undefined)[]
  mezzanineSf?: (number | undefined)[]
  mezzanineHvac?: string
  warehouseSf?: (number | undefined)[]
  warehouseHvac?: string
  freightDockNumber?: (number | undefined)[]
  // TODO implement boolean filters
  yard?: string
  yardFenced?: string
  yardPaved?: string

  // Multifamily
  totalUnits?: (number | undefined)[]
}

export function sanitizeAnonymousMapFilters(
  filters: MapFiltersType
): MapFiltersType {
  const statuses = filters.statuses?.filter((status) =>
    PUBLIC_MAP_STATUSES.includes(status as (typeof PUBLIC_MAP_STATUSES)[number])
  )
  const sanitizedFilters: MapFiltersType = {
    ...filters,
    statuses: statuses?.length ? statuses : [...PUBLIC_MAP_STATUSES],
  }

  delete sanitizedFilters.transacted

  if (sanitizedFilters.sort?.startsWith('offMarket')) {
    delete sanitizedFilters.sort
  }

  return sanitizedFilters
}

type MapFiltersUpdate =
  | MapFiltersType
  | ((currentFilters: MapFiltersType) => MapFiltersType)

const exactValueFields = [
  'sort',
  'search',
  'addressSearch',
  'rateType',
  'priceType',
  'lotSizeUnit',
  'mezzanineHvac',
  'warehouseHvac',
  'yard',
  'yardFenced',
  'yardPaved',
  'business',
]

const stringFields = ['companies', 'brokers', 'postalCodes', 'propertySubtypes']
const dateFields = ['created', 'modified', 'transacted']

export const aPersistedFilters = atomWithStorage<MapFiltersType | null>(
  'persistedFilters',
  null
)

export const aMapFilters = atom(
  (get): MapFiltersType => {
    const defaultMapFilters = get(aDefaultMapFilters)
    const user = get(aUser)
    let searchParams =
      get(aMapLocation).searchParams && get(aMapLocation).searchParams!.size > 0
        ? get(aMapLocation).searchParams
        : null
    const initialMapSearch = get(aInitialMapSearch)
    if (
      !searchParams &&
      typeof window === 'undefined' &&
      initialMapSearch
    ) {
      searchParams = new URLSearchParams(initialMapSearch)
    }
    if (!searchParams && typeof window !== 'undefined') {
      searchParams = new URLSearchParams(window.location.search)
    }

    const shouldUsePersisted =
      !searchParams?.has('savedSearch') && !!user?.options?.map?.preserve

    let filters: { [key: string]: any } = Object.assign({}, defaultMapFilters)

    if (typeof window !== 'undefined' && shouldUsePersisted) {
      try {
        const persistedFilters = JSON.parse(
          localStorage.getItem('persistedFilters') ?? ''
        )

        if (persistedFilters && Object.keys(persistedFilters).length > 0) {
          filters = Object.assign(filters, persistedFilters)
        }
      } catch {
        //
      }
    }

    if (searchParams) {
      for (const [key, value] of searchParams.entries()) {
        if (key === 'savedSearch') {
          continue
        }
        if (['lat', 'lng', 'z', 'b', 'p'].includes(key)) {
          if (value != '0') {
            filters[key] = value
          }
        } else if (value) {
          if (exactValueFields.includes(key)) {
            filters[key] = value
          } else if (dateFields.includes(key)) {
            filters[key] = value.split('_').map((v) => v || undefined)
          } else {
            filters[key] = value.split('_').map((v) => {
              if (stringFields.includes(key)) {
                return v
              }
              // FIXME: this doesn't handle min/max number ranges correctly (eg. prices, rates, etc.)
              //        in cases where min is set and max is not or max is set and min is not
              //        leaving one side of the range empty parses into an empty string
              //        for example, &prices=200000_ parses to ["200000", ""]
              //        where the max is an empty string
              //        when parsed to a number this empty string is NaN
              //        as written this implementation will return the empty string value
              //        rather than number or undefined
              //        but typescript assumes the parsed result will be (number | undefined)[]
              //
              //        we should probably return undefined here if number parsing fails
              //        leaving for now to avoid breaking filters which depend on this current logic
              const parsed = parseFloat(v)
              return Number.isNaN(parsed) ? v : parsed
            })
          }
        }
      }
    }
    return user ? filters : sanitizeAnonymousMapFilters(filters)
  },
  (get, set, filtersUpdate: MapFiltersUpdate) => {
    const currentFilters = get(aMapFilters)
    const filters =
      typeof filtersUpdate === 'function'
        ? filtersUpdate(currentFilters)
        : filtersUpdate

    if (filters === currentFilters) {
      return
    }

    const params = getParamsFromMapFilters(filters)
    const mapLocation = get(aMapLocation)
    const pathname =
      typeof window !== 'undefined'
        ? window.location.pathname
        : (mapLocation.pathname ?? '/')
    const isMapCollection = isMapCollectionPathname(pathname)

    set(aMapLocation, {
      pathname: isMapCollection ? buildMapRootPath(pathname) : pathname,
      searchParams: new URLSearchParams(params),
    })
  }
)

export function getParamsFromMapFilters(filters: { [key: string]: any }) {
  const params: [string, string][] = []
  // Compare filters to default and exclude if the same
  Object.keys(filters).forEach((key) => {
    if (filters[key] && !['lat', 'lng', 'z', 'b', 'p'].includes(key)) {
      if (Array.isArray(filters[key])) {
        if (filters[key].filter((v?: any) => v).length > 0) {
          if (dateFields.includes(key)) {
            params.push([
              key,
              filters[key].map((v) => v?.split('T')[0]).join('_'),
            ])
          } else {
            params.push([key, filters[key].join('_')])
          }
        }
      } else {
        params.push([key, filters[key]])
      }
    }
  })
  if (typeof filters.b !== 'undefined' && filters.b != 0) {
    params.unshift(['b', filters.b])
  }
  if (typeof filters.p !== 'undefined' && filters.p != 0) {
    params.unshift(['p', filters.p])
  }
  if (typeof filters.z !== 'undefined') {
    params.unshift(['z', filters.z])
  }
  if (
    typeof filters.lat !== 'undefined' &&
    typeof filters.lng !== 'undefined'
  ) {
    params.unshift(['lat', filters.lat])
    params.unshift(['lng', filters.lng])
  }
  return params
}

const getFindRange = (minValue?: number, maxValue?: number) => {
  const f: { $gte?: number; $lte?: number } = {}
  if (minValue) {
    f['$gte'] = minValue
  }
  if (maxValue) {
    f['$lte'] = maxValue
  }
  return Object.keys(f).length === 0 ? undefined : f
}

type MinMaxRange<MinKey extends string, MaxKey extends string, T = number> =
  | { [K in MaxKey]: { $gte: T } }
  | { [K in MinKey]: { $lte: T } }
  | {
      $or: [
        {
          $and: [{ [K in MinKey]: { $gte: T } }, { [K in MinKey]: { $lte: T } }]
        },
        {
          $and: [{ [K in MaxKey]: { $gte: T } }, { [K in MaxKey]: { $lte: T } }]
        },
        {
          $and: [{ [K in MinKey]: { $lte: T } }, { [K in MaxKey]: { $gte: T } }]
        },
      ]
    }

function getMinMaxRange<MinKey extends string, MaxKey extends string>(
  minKey: MinKey,
  maxKey: MaxKey,
  min?: number,
  max?: number
): MinMaxRange<MinKey, MaxKey> | undefined {
  if (min !== undefined && max !== undefined) {
    return {
      $or: [
        {
          $and: [{ [minKey]: { $gte: min } }, { [minKey]: { $lte: max } }],
        },
        {
          $and: [{ [maxKey]: { $gte: min } }, { [maxKey]: { $lte: max } }],
        },
        {
          $and: [{ [minKey]: { $lte: min } }, { [maxKey]: { $gte: max } }],
        },
      ],
    } as MinMaxRange<MinKey, MaxKey>
  } else if (min) {
    return { [maxKey]: { $gte: min } } as MinMaxRange<MinKey, MaxKey>
  } else if (max) {
    return { [minKey]: { $lte: max } } as MinMaxRange<MinKey, MaxKey>
  }
}

type MapFiltersPriceFind = {
  $or?: {
    $and?: {
      'filters.price'?: { $gte?: number; $lte?: number }
      'filters.priceMax'?: { $gte?: number; $lte?: number }
    }[]
  }[]
  'filters.price'?: { $lte?: number }
  'filters.priceMax'?: { $gte?: number }
}

type MapFiltersSaleFind =
  | {
      type: { $in: string[] }
      // 'filters.price'?: { $gte?: number; $lte?: number }
      'filters.capRate'?: { $gte?: number; $lte?: number }
    }
  | MapFiltersPriceFind

type MapFiltersLeaseRateFind = {
  $or?: {
    $and?: {
      'filters.minRate'?: { $gte?: number; $lte?: number }
      'filters.maxRate'?: { $gte?: number; $lte?: number }

      'filters.minRateHistoric'?: { $gte?: number; $lte?: number }
      'filters.maxRateHistoric'?: { $gte?: number; $lte?: number }
    }[]
    // 'filters.minRate'?: { $gte?: number; $lte?: number }
    // 'filters.maxRate'?: { $gte?: number; $lte?: number }
  }[]
  'filters.maxRate'?: { $gte?: number }
  'filters.minRate'?: { $lte?: number }

  'filters.maxRateHistoric'?: { $gte?: number }
  'filters.minRateHistoric'?: { $lte?: number }
}

type MapFiltersLeaseAvailableSfFind = {
  $or?: {
    $and?: {
      'filters.minSf'?: { $gte?: number; $lte?: number }
      'filters.maxSf'?: { $gte?: number; $lte?: number }

      'filters.minSfHistoric'?: { $gte?: number; $lte?: number }
      'filters.maxSfHistoric'?: { $gte?: number; $lte?: number }
    }[]
    // 'filters.minSf'?: { $gte?: number; $lte?: number }
    // 'filters.maxSf'?: { $gte?: number; $lte?: number }
  }[]
  'filters.maxSf'?: { $gte?: number }
  'filters.minSf'?: { $lte?: number }

  'filters.maxSfHistoric'?: { $gte?: number }
  'filters.minSfHistoric'?: { $lte?: number }
}

type MapFiltersLeaseFind = {
  // lease, saleLease
  type: { $in: string[] }
  $and?: (MapFiltersLeaseRateFind | MapFiltersLeaseAvailableSfFind)[]
}

type LeaseSpaceRange = {
  $elemMatch: {
    available?: boolean
    min?: { $gte?: number; $lte?: number }
    max?: { $gte?: number; $lte?: number }
    $or?: {
      $and?: {
        min?: { $gte?: number; $lte?: number }
        max?: { $gte?: number; $lte?: number }
      }[]
    }[]
  }
}

type MapFiltersTypesFind =
  | {
      $or: (MapFiltersSaleFind | MapFiltersLeaseFind)[]
    }
  | {
      'filters.acres'?: LeaseSpaceRange
      'filters.lotSizeAcres'?: LeaseSpaceRange
      'filters.lotSizeSf'?: LeaseSpaceRange
    }
  | {
      'filters.transactionDates'?: {
        $elemMatch: {
          $gte?: Date
          $lt?: Date
          $lte?: Date
        }
      }
    }
  | {
      'filters.buildingSf'?: { $gte?: number; $lte?: number }
      // Sort
      'filters.price'?: { $ne: null }
      'filters.capRate'?: { $ne: null }
      'filters.acres'?: { $ne: null }
      'filters.bsf'?: { $ne: null }
      'filters.minRate'?: { $ne: null }
      // 'filters.maxRate'?: { $ne: null }
      'filters.minSf'?: { $ne: null }
      // { 'filters.maxSf': { $ne: null } }?,
    }
  | MinMaxRange<'meta.ceilingHeightMin', 'meta.ceilingHeightMax'>
  // Industrial
  | {
      'meta.officeCeilingHeight'?: { $gte?: number; $lte?: number }
      'meta.trailerParkingSpaces'?: { $gte?: number; $lte?: number }
      'meta.eaveHeight': { $gte?: number; $lte?: number }
      'meta.overheadCranes': { $gte?: number; $lte?: number }
      'meta.railDoors': { $gte?: number; $lte?: number }
      'meta.mezzanineSf': { $gte?: number; $lte?: number }
      'meta.freightDockNumber': { $gte?: number; $lte?: number }
      'filters.officeSf'?: LeaseSpaceRange
      'filters.dockNumber'?: LeaseSpaceRange
      'filters.driveInNumber'?: LeaseSpaceRange
      'filters.clearHeight'?: LeaseSpaceRange
      'filters.warehouseSf'?: LeaseSpaceRange
    }
  // Multifamily
  | {
      'meta.totalUnits'?: { $gte?: number; $lte?: number }
    }

type MapFiltersFind = Filter<Listing> & {
  $text?: { $search: string; $caseSensitive: boolean }
  status?: { $in: string[] }
  'property.postalCode'?: { $in: string[] }
  'property.city'?: { $in: string[] }
  'property.county'?: { $in: string[] }
  'property.stateAbbr'?: { $in: string[] }
  propertyTypes?: { $in: string[] }
  propertySubtypes?: { $in: string[] }
  'meta.submarket'?: { $in: string[] }
  'meta.zoning'?: { $regex: string }
  'company.tid'?: { $in: string[] }
  'brokers.tid'?: { $in: string[] }

  activeDate?: { $gte?: string; $lte?: string }
  modified?: { $gte?: string; $lte?: string }
  offMarketDate?: { $gte?: string; $lte?: string; $ne?: null } // Use for sort

  // yard?: string //boolean
  // yardFenced?: string //boolean
  // yardPaved?: string //boolean

  // For sort
  'filters.price'?: { $ne: null }
  'filters.maxRate'?: { $ne: null }
  'filters.minRate'?: { $ne: null }
  'filters.maxSf'?: { $ne: null }
  'filters.minSf'?: { $ne: null }
  'filters.maxAcres'?: { $ne: null }
  'filters.minAcres'?: { $ne: null }
  'filters.minTransactionDate'?: { $ne: null } | { $exists: true }
  'filters.maxTransactionDate'?: { $ne: null }

  $and: MapFiltersTypesFind[]
}

function getIndustrialFilters(filters: MapFiltersType, leased: boolean) {
  const $and = []
  const clearHeightRange = getLeaseSpaceRangeFind(
    'filters.clearHeight',
    leased,
    filters.clearHeight
  )
  if (clearHeightRange) {
    $and.push(clearHeightRange)
  }

  const officeSfRange = getLeaseSpaceRangeFind(
    'filters.officeSf',
    leased,
    filters.officeSf
  )
  if (officeSfRange) {
    $and.push(officeSfRange)
  }

  const find: Record<string, { $gte?: number; $lte?: number }> = {}
  const officeCeilingRange = getFindRange(
    filters.officeCeilingHeight?.[0],
    filters.officeCeilingHeight?.[1]
  )
  if (officeCeilingRange) {
    find['meta.officeCeilingHeight'] = officeCeilingRange
  }
  const trailerParkingSpaces = getFindRange(
    filters.trailerParkingSpaces?.[0],
    filters.trailerParkingSpaces?.[1]
  )
  if (trailerParkingSpaces) {
    find['meta.trailerParkingSpaces'] = trailerParkingSpaces
  }
  const eaveHeight = getFindRange(
    filters.eaveHeight?.[0],
    filters.eaveHeight?.[1]
  )
  if (eaveHeight) {
    find['meta.eaveHeight'] = eaveHeight
  }
  const overheadCranes = getFindRange(
    filters.overheadCranes?.[0],
    filters.overheadCranes?.[1]
  )
  if (overheadCranes) {
    find['meta.overheadCranes'] = overheadCranes
  }
  const railDoors = getFindRange(filters.railDoors?.[0], filters.railDoors?.[1])
  if (railDoors) {
    find['meta.railDoors'] = railDoors
  }
  const mezzanineSf = getFindRange(
    filters.mezzanineSf?.[0],
    filters.mezzanineSf?.[1]
  )
  if (mezzanineSf) {
    find['meta.mezzanineSf'] = mezzanineSf
  }
  const freightDockNumber = getFindRange(
    filters.freightDockNumber?.[0],
    filters.freightDockNumber?.[1]
  )
  if (freightDockNumber) {
    find['meta.freightDockNumber'] = freightDockNumber
  }

  // 'meta.officeCeilingHeight'?: { $gte?: number; $lte?: number }
  // 'meta.trailerParkingSpaces'?: { $gte?: number; $lte?: number }
  // 'meta.eaveHeight': { $gte?: number; $lte?: number }
  // 'meta.overheadCranes': { $gte?: number; $lte?: number }
  // 'meta.railDoors': { $gte?: number; $lte?: number }
  // 'meta.mezzanineSf': { $gte?: number; $lte?: number }
  // 'meta.freightDockNumber': { $gte?: number; $lte?: number }

  const dockNumberRange = getLeaseSpaceRangeFind(
    'filters.dockNumber',
    leased,
    filters.dockNumber
  )
  if (dockNumberRange) {
    $and.push(dockNumberRange)
  }

  const driveInNumberRange = getLeaseSpaceRangeFind(
    'filters.driveInNumber',
    leased,
    filters.driveInNumber
  )
  if (driveInNumberRange) {
    $and.push(driveInNumberRange)
  }

  const warehouseSfRange = getLeaseSpaceRangeFind(
    'filters.warehouseSf',
    leased,
    filters.warehouseSf
  )
  if (warehouseSfRange) {
    $and.push(warehouseSfRange)
  }

  if (Object.keys(find).length > 0) {
    $and.push(find)
  }
  // cast as MapFiltersRangeFind[] to type filter correctly
  return $and.filter((v) => v) //as MapFiltersRangeFind[]
}

function getPriceKeys(priceType: NonNullable<MapFiltersType['priceType']>) {
  if (priceType === 'sf') {
    return {
      priceMinKey: 'filters.pricePerSf.min',
      priceMaxKey: 'filters.pricePerSf.max',
    }
  } else if (priceType === 'acre') {
    return {
      priceMinKey: 'filters.pricePerAcre.min',
      priceMaxKey: 'filters.pricePerAcre.max',
    }
  } else {
    return {
      priceMinKey: 'filters.priceTotal.min',
      priceMaxKey: 'filters.priceTotal.max',
    }
  }
}

function toNum(value: number | string | undefined): number | undefined {
  if (value === undefined || typeof value === 'number') return value
  if (value === '') return undefined

  return Number(value)
}

function getPriceFind(filters: MapFiltersType): MapFiltersPriceFind {
  if (filters.prices && filters.prices.length) {
    const priceType = filters.priceType || 'total'
    const { priceMinKey, priceMaxKey } = getPriceKeys(priceType)
    const [priceMin, priceMax] = filters.prices

    return (
      getMinMaxRange(
        priceMinKey,
        priceMaxKey,
        toNum(priceMin),
        toNum(priceMax)
      ) ?? {}
    )
  } else {
    return {}
  }
}

function getSaleLeaseFilters(filters: MapFiltersType) {
  const $or = []
  if (filters.types!.includes('sale')) {
    const sale: MapFiltersSaleFind = {
      type: { $in: ['sale', 'saleLease'] },
      ...getPriceFind(filters),
    }
    if ((filters.caps || []).length > 0) {
      sale['filters.capRate'] = getFindRange(filters.caps![0], filters.caps![1])
    }
    $or.push(sale)
  }
  if (filters.types!.includes('lease')) {
    const $and = []

    const leased =
      filters.statuses !== undefined && filters.statuses.includes('leased')

    if (filters.rateType === 'totalMonthly') {
      const ratesFind = getLeaseSpaceRangeFind(
        'filters.totalMonthlyRates',
        leased,
        filters.rates
      )
      if (ratesFind) {
        $and.push(ratesFind)
      }
    } else {
      const ratesFind = getRateFind(leased, filters.rates, filters.rateType)
      if (ratesFind) {
        $and.push(ratesFind)
      }
    }

    const sfFind = getLeaseSpaceRangeFind('filters.sf', leased, filters.sf)
    if (sfFind) {
      $and.push(sfFind)
    }

    const lease: MapFiltersLeaseFind = {
      type: { $in: ['lease', 'saleLease'] },
      // $and: $and.length > 0 ? $and : undefined,
    }
    if ($and.length > 0) {
      lease.$and = $and
    }
    $or.push(lease)
  }
  return $or
}

function getLeaseSpaceRangeFind(
  key: string,
  leased: boolean,
  range?: (number | undefined)[]
) {
  if (!range || !range.length) return

  const [min, max] = range
  const available = leased ? {} : { available: true }

  if (min && max) {
    return {
      [key]: {
        $elemMatch: {
          ...available,
          $or: [
            {
              $and: [{ min: { $gte: min } }, { min: { $lte: max } }],
            },
            {
              $and: [{ max: { $gte: min } }, { max: { $lte: max } }],
            },
            {
              $and: [{ min: { $lte: min } }, { max: { $gte: max } }],
            },
          ],
        },
      },
    }
  } else if (min) {
    return {
      [key]: {
        $elemMatch: {
          ...available,
          max: { $gte: min },
        },
      },
    }
  } else if (max) {
    return {
      [key]: {
        $elemMatch: {
          ...available,
          min: { $lte: max },
        },
      },
    }
  }
}

function yearlyToMonthly(value?: number): number | undefined {
  return value === undefined ? undefined : value / 12
}

function getRateFind(
  leased: boolean,
  range?: (number | undefined)[],
  rateType?: RateType
) {
  if (!range || !range.length) return

  const [min, max] = range
  const isMonthly = rateType === 'monthly'
  const newMin = isMonthly ? yearlyToMonthly(min) : min
  const newMax = isMonthly ? yearlyToMonthly(max) : max
  const newRange = [newMin, newMax]

  return getLeaseSpaceRangeFind('filters.rates', leased, newRange)
}

function getTransactionDateFind(filters: MapFiltersType) {
  if (!filters.transacted || !filters.transacted.length) return

  const key = 'filters.transactionDates'
  const [min, max] = filters.transacted
  const startDate = min ? new Date(min) : undefined
  const endDateExclusive = max ? new Date(max) : undefined

  if (endDateExclusive) {
    endDateExclusive.setUTCDate(endDateExclusive.getUTCDate() + 1)
  }

  if (startDate && endDateExclusive) {
    return {
      [key]: {
        $elemMatch: {
          $gte: startDate,
          $lt: endDateExclusive,
        },
      },
    }
  } else if (startDate) {
    return {
      [key]: { $elemMatch: { $gte: startDate } },
    }
  } else if (endDateExclusive) {
    return {
      [key]: { $elemMatch: { $lt: endDateExclusive } },
    }
  }
}

function formatDateString(date: Date | string): string {
  const asString = date instanceof Date ? date.toISOString() : date

  return asString.split('T')[0]
}

function getNextDateString(date: Date | string): string {
  const [year, month, day] = formatDateString(date).split('-').map(Number)
  const nextDay = new Date(Date.UTC(year, month - 1, day))
  nextDay.setUTCDate(nextDay.getUTCDate() + 1)

  return nextDay.toISOString().split('T')[0]
}

function getArrayDateFilter(
  leased: boolean,
  dateRange?: (Date | string | undefined)[]
) {
  if (!dateRange || !dateRange.length) return

  const [min, max] = dateRange
  const available = leased ? {} : { available: true }

  if (min && max) {
    return {
      $elemMatch: {
        ...available,
        date: {
          $gte: formatDateString(min),
          $lt: getNextDateString(max),
        },
      },
    }
  } else if (min) {
    return {
      $elemMatch: {
        ...available,
        date: { $gte: formatDateString(min) },
      },
    }
  } else if (max) {
    return {
      $elemMatch: {
        ...available,
        date: { $lt: getNextDateString(max) },
      },
    }
  }
}

export function getFindFromFilters(filters: MapFiltersType) {
  // Others
  const $and: MapFiltersTypesFind[] = [
    { $or: getSaleLeaseFilters(filters) }, // lease and sale specific
  ]

  const leased =
    filters.statuses !== undefined && filters.statuses.includes('leased')
  const acresFind = getLeaseSpaceRangeFind(
    filters.lotSizeUnit === 'sf' ? 'filters.lotSizeSf' : 'filters.lotSizeAcres',
    leased,
    filters.acres
  )
  if (acresFind) {
    $and.push(acresFind)
  }
  if ((filters.bsf || []).length > 0) {
    $and.push({
      'filters.buildingSf': getFindRange(filters.bsf![0], filters.bsf![1]),
    })
  }
  const ceilingHeightRange = getMinMaxRange<
    'meta.ceilingHeightMin',
    'meta.ceilingHeightMax'
  >(
    'meta.ceilingHeightMin',
    'meta.ceilingHeightMax',
    filters.ceilingHeight?.[0],
    filters.ceilingHeight?.[1]
  )
  if (ceilingHeightRange) {
    $and.push(ceilingHeightRange)
  }

  const industrialFilters = getIndustrialFilters(filters, leased)
  if (industrialFilters.length > 0) {
    $and.push(...industrialFilters)
  }

  // Multifamily filters
  if ((filters.totalUnits || []).length > 0) {
    $and.push({
      'meta.totalUnits': getFindRange(
        filters.totalUnits![0],
        filters.totalUnits![1]
      ),
    })
  }

  const transactionDateFind = getTransactionDateFind(filters)
  if (transactionDateFind) {
    $and.push(transactionDateFind)
  }

  const find: MapFiltersFind = {
    // type: { $in: [...filters.types!, 'saleLease'] },
    status: { $in: filters.statuses! },
    $and,
  }

  if (filters.search && filters.search.trim().length > 0) {
    find.$text = { $search: filters.search.trim(), $caseSensitive: false }
  }
  if (filters.addressSearch && filters.addressSearch.trim().length > 0) {
    const variations = generateAddressVariations(filters.addressSearch.trim())

    if (variations.length > 1) {
      find.$and.push({
        $or: variations.map((variation) => ({
          'property.address': {
            $regex: escapeRegex(variation),
            $options: 'i',
          },
        })),
      })
    } else {
      find['property.address'] = {
        $regex: escapeRegex(filters.addressSearch.trim()),
        $options: 'i',
      }
    }
  }
  const propertyUseFind = buildMapPropertyUseClause(
    filters.propertyTypes,
    filters.propertySubtypes
  )
  if (propertyUseFind.propertyTypes) {
    find.propertyTypes = propertyUseFind.propertyTypes
  }
  if (propertyUseFind.propertyUseClause) {
    find.$and.push(propertyUseFind.propertyUseClause)
  }

  if ((filters.submarkets || []).length > 0) {
    find['meta.submarket'] = { $in: filters.submarkets! }
  }

  if ((filters.postalCodes || []).length > 0) {
    find['property.postalCode'] = {
      $in: filters.postalCodes!,
    }
  }
  if ((filters.cities || []).length > 0) {
    find['property.city'] = {
      $in: Array.from(
        new Set(filters.cities!.flatMap(generateCityNameVariations))
      ),
    }
  }
  if ((filters.counties || []).length > 0) {
    find['property.county'] = { $in: filters.counties! }
  }
  if ((filters.states || []).length > 0) {
    find['property.stateAbbr'] = { $in: filters.states! }
  }
  if ((filters.zoning || []).length > 0) {
    find['meta.zoning'] = { $regex: filters.zoning!.join('|') }
  }
  if ((filters.companies || []).length > 0) {
    find['company.tid'] = { $in: filters.companies! }
  }
  if ((filters.brokers || []).length > 0) {
    find['brokers.tid'] = { $in: filters.brokers! }
  }
  if (filters.business === 'hide') {
    find['isBusinessListing'] = { $exists: false }
  } else if (filters.business === 'true') {
    find['isBusinessListing'] = true
  }

  if (filters.sort) {
    if (filters.sort.includes('price')) {
      find['filters.price'] = { $ne: null }
    } else if (filters.sort.includes('rateD')) {
      find['filters.maxRate'] = { $ne: null }
    } else if (filters.sort.includes('rateA')) {
      find['filters.minRate'] = { $ne: null }
    } else if (filters.sort === 'availableSfD') {
      find['filters.maxSf'] = { $ne: null }
    } else if (filters.sort === 'availableSfA') {
      find['filters.minSf'] = { $ne: null }
    } else if (filters.sort.includes('acresD')) {
      find['filters.maxAcres'] = { $ne: null }
    } else if (filters.sort.includes('acresA')) {
      find['filters.minAcres'] = { $ne: null }
    } else if (filters.sort.includes('offMarketD')) {
      find['filters.maxTransactionDate'] = { $ne: null }
    } else if (filters.sort.includes('offMarketA')) {
      find['filters.minTransactionDate'] = { $exists: true }
    }
  }

  const createdFilter = getArrayDateFilter(leased, filters.created)

  if (createdFilter) {
    find['filters.created'] = createdFilter
  }

  const modifiedFilter = getArrayDateFilter(leased, filters.modified)

  if (modifiedFilter) {
    find['filters.modified'] = modifiedFilter
  }

  return find
}

export const aFiltersFind = atom((get) => {
  const filters = get(aMapFilters)
  return getFindFromFilters(filters)
})

export const aFiltersSort = atom((get) => {
  const filters = get(aMapFilters)
  return getSortFromFilters(filters)
})

export function getSortFromFilters(filters: MapFiltersType) {
  let sort: Sort = { coreModified: -1 }
  const sortKeys: { [key: string]: string } = {
    modifiedD: 'coreModified',
    createdD: 'activeDate',
    offMarketD: 'filters.maxTransactionDate',
    offMarketA: 'filters.minTransactionDate',
    availableSfD: 'filters.maxSf',
    availableSfA: 'filters.minSf',
    priceD: 'filters.price',
    priceA: 'filters.price',
    rateD: 'filters.maxRate',
    rateA: 'filters.minRate',
    acresD: 'filters.maxAcres',
    acresA: 'filters.minAcres',
  }
  if (filters.sort) {
    const direction = filters.sort.slice(filters.sort.length - 1)
    if (
      typeof sortKeys[filters.sort] !== 'undefined' &&
      (direction === 'D' || direction === 'A')
    ) {
      sort = { [sortKeys[filters.sort]]: direction === 'D' ? -1 : 1 }
    }
  }
  return sort
}
